import { Box, IconButton, Typography } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import type { Control } from "react-hook-form"

import { StatusIndicator } from "./StatusIndicator"
import type { UploadRowStatus } from "./types"

import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { FileTypeIcon } from "@/components/upload/FileTypeIcon"
import { formatFileSize } from "@/components/upload/utils"
import type { UploadRowProgress } from "@/hooks/useMultiFileUpload"
import type { Option } from "@/types/form-fields"

const getFileExtension = (fileName: string): string => fileName.split(".").pop()?.toUpperCase() ?? ""

interface MultiFileUploadRowProps {
  control: Control<any>
  name: string
  index: number
  file: File
  documentTypeOptions?: Option<string>[]
  withDescription?: boolean
  status?: UploadRowStatus
  progress?: UploadRowProgress
  isUploading?: boolean
  onRemove: () => void
  testIdPrefix: string
}

export const MultiFileUploadRow = ({
  control,
  name,
  index,
  file,
  documentTypeOptions,
  withDescription = false,
  status,
  progress,
  isUploading = false,
  onRemove,
  testIdPrefix,
}: MultiFileUploadRowProps): ReactElement => {
  return (
    <Box
      className='grid items-center gap-4 rounded border border-gray-400 p-4'
      sx={{
        gridTemplateColumns: [
          "auto",
          "minmax(0, 1fr)",
          documentTypeOptions ? "200px" : "",
          withDescription ? "minmax(0, 1fr)" : "",
          status ? "auto" : "",
          "auto",
        ]
          .filter(Boolean)
          .join(" "),
      }}
    >
      <FileTypeIcon fileName={file.name} className='size-12 shrink-0' />
      <Box className='min-w-0'>
        <Typography variant='body2' className='truncate font-medium text-gray-900' title={file.name}>
          {file.name}
        </Typography>
        <Typography variant='caption' className='text-gray-500'>
          {formatFileSize(file.size)} &middot; {getFileExtension(file.name)}
        </Typography>
      </Box>
      {documentTypeOptions && (
        <FormSelect<any, string, string>
          control={control}
          name={`${name}.${index}.documentType`}
          label='Document Type'
          required
          placeholder='Select One'
          options={documentTypeOptions}
          data-testid={`${testIdPrefix}.file-type-select-${index}`}
        />
      )}
      {withDescription && (
        <FormTextField
          control={control}
          name={`${name}.${index}.description`}
          label='Document Description'
          data-testid={`${testIdPrefix}.description-field-${index}`}
        />
      )}
      {status && <StatusIndicator status={status} progress={progress} />}
      <IconButton
        onClick={onRemove}
        disabled={isUploading}
        data-testid={`${testIdPrefix}.remove-button-${index}`}
        className='shrink-0 justify-self-end text-gray-900'
        size='small'
      >
        <DeleteOutlined />
      </IconButton>
    </Box>
  )
}
